import React, { FC, useEffect } from 'react';
import { ScrollView, View } from 'react-native';
import Loading from '@components/loading';
import { scale } from '@components/frame';
import { SCREEN_PADDING_HORIZONTAL } from '@v2/pages/common/constants/base.constants';
import { isPhone, navigateTo } from '@helpers';
import { getBoxShadowStyle, getElevation } from '@styles/shadow';
import Line from '@components/common/line';
import AnimatedTable from '@components/settings/components/common/animatedTable';
import { connect } from 'react-redux';
import { getMachines, getMachinesReferencesBySourcesSystem } from '@reducers/machines';
import { Machine, MachineReferenceWithMachineIds } from '@interfaces/machines';
import { ColorFunction } from '@styles/colors/types';
import i18n from 'i18n-js';

interface ImnooSFMachineMapItem {
  imnooMachine: string;
  smartFactoryMachine: string;
  smartFactoryMachineId?: number;
}

export enum MachineImnooPerspective {
  IMNOO = 'Imnoo',
  SF = 'Smart Factory',
}

export interface MachinesImnooProps {
  C: ColorFunction;
  getMachinesReferencesBySourcesSystem: (sourceSystem: string) => Promise<void>;
  machinesReferences: MachineReferenceWithMachineIds[];
  machinesReferencesLoaded: boolean;
  machines: Machine[];
  getMachines: () => Promise<Machine[]>;
  machinesLoaded: boolean;
}

const MachinesImnoo: FC<MachinesImnooProps> = ({
  C,
  getMachinesReferencesBySourcesSystem,
  machinesReferences,
  machinesReferencesLoaded,
  machines,
  getMachines,
  machinesLoaded,
}) => {
  useEffect(() => {
    if (!machinesLoaded) {
      getMachines();
    }
  }, [machinesLoaded]);

  useEffect(() => {
    getMachinesReferencesBySourcesSystem('Imnoo');
  }, []);

  if (!machinesReferencesLoaded) {
    return (
      <View style={{ width: '100%', height: '70%', justifyContent: 'center', alignContent: 'center', alignItems: 'center' }}>
        <Loading background={'transparent'} />
      </View>
    );
  }

  const notLinkedMachineValue = i18n.t('Not Selected');

  const getMachinePerspective = (item: ImnooSFMachineMapItem): MachineImnooPerspective => {
    const imnooMachineNotLinked = i18n.t(item.imnooMachine) === notLinkedMachineValue;

    return imnooMachineNotLinked ? MachineImnooPerspective.SF : MachineImnooPerspective.IMNOO;
  };

  const openMachineImnooDetails = (i: ImnooSFMachineMapItem) => {
    const machinePerspective = getMachinePerspective(i);
    const sfPerspective = machinePerspective === MachineImnooPerspective.SF;
    const pressedMachineId = sfPerspective ? i.smartFactoryMachineId : null;
    const machineName = sfPerspective ? i.smartFactoryMachine : i.imnooMachine;
    const selectedMachine =
      sfPerspective && i18n.t(i.imnooMachine) !== notLinkedMachineValue
        ? i.imnooMachine
        : i18n.t(i.smartFactoryMachine) !== notLinkedMachineValue
        ? i.smartFactoryMachine
        : null;

    navigateTo('Settings', {
      screen: 'Machines',
      params: {
        screen: 'MachinesImnooDetails',
        params: {
          selectedMachine,
          machineName,
          machinePerspective,
          ...(machinePerspective === MachineImnooPerspective.SF ? { machineId: pressedMachineId } : {}),
        },
      },
    });
  };

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  const getParsedMachinesReferences = (): ImnooSFMachineMapItem[] => {
    let notLinkedToImnooMachineNamesAndIds = machines.map((m) => ({ name: m.shortName, id: m.id }));

    const linkedToImnooMachineList = machinesReferences.reduce((acc: ImnooSFMachineMapItem[], i) => {
      let linkedMachineName = '';

      const linkedMachine = machines.find((m) => m.id === i.machines[0]?.machineId);
      if (linkedMachine) {
        notLinkedToImnooMachineNamesAndIds = notLinkedToImnooMachineNamesAndIds.filter((m) => m.id !== linkedMachine?.id);
      }
      linkedMachineName = linkedMachine?.shortName || notLinkedMachineValue;

      acc.push({
        imnooMachine: i.name,
        smartFactoryMachine: linkedMachineName,
        smartFactoryMachineId: linkedMachine?.id,
      });

      return acc;
    }, []);

    notLinkedToImnooMachineNamesAndIds.forEach((machine) => {
      linkedToImnooMachineList.push({ imnooMachine: notLinkedMachineValue, smartFactoryMachine: machine.name, smartFactoryMachineId: machine.id });
    });

    return linkedToImnooMachineList;
  };

  const parsedMachinesReferences = getParsedMachinesReferences();

  return (
    <View style={{ width: '100%', flex: 1 }}>
      <ScrollView style={{ width: '100%', flex: 1 }} contentContainerStyle={{ paddingBottom: scale(50), paddingTop: scale(2) }}>
        <View
          style={{
            paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL),
          }}
        >
          {isPhone ? (
            <View
              style={{
                elevation: getElevation('Field'),
                ...shadowStyles,
                borderRadius: scale(12),
              }}
            >
              {!!parsedMachinesReferences &&
                parsedMachinesReferences.map((i, index) => (
                  <Line
                    key={index}
                    {...{
                      item: { name: i.imnooMachine, value: i.smartFactoryMachine, noArrow: false, onClick: () => openMachineImnooDetails(i) },
                      valueColor: 'Label/Light/Tertiary',
                      alwaysTwoLines: true,
                      nameAlreadyTranslated: true,
                      valueAlreadyTranslated: true,
                      index,
                      array: parsedMachinesReferences,
                      bigSize: true,
                      C,
                      topBorder: index !== 0,
                      roundTop: index === 0,
                      roundBottom: index === parsedMachinesReferences?.length - 1,
                    }}
                  />
                ))}
            </View>
          ) : (
            <AnimatedTable
              {...{
                C,
                valuesAlreadyTranslated: true,
                loaded: machinesReferencesLoaded,
                array: parsedMachinesReferences,
                headerSize: 50,
                lineSize: 63,
                showMoreSize: 63,
                onPress: openMachineImnooDetails,
                config: [
                  {
                    key: 'imnooMachine',
                    title: 'Imnoo Machine',
                    width: '50%',
                    titleColor: C('Label/Light/Secondary'),
                    getDynamicColor: (item: ImnooSFMachineMapItem) =>
                      i18n.t(item.imnooMachine) === notLinkedMachineValue ? C('Label/Light/Tertiary') : C('Label/Light/Primary'),
                  },
                  {
                    key: 'smartFactoryMachine',
                    title: 'Smart Factory Machine',
                    width: '50%',
                    titleColor: C('Label/Light/Secondary'),
                    getDynamicColor: (item: ImnooSFMachineMapItem) =>
                      i18n.t(item.smartFactoryMachine) === notLinkedMachineValue ? C('Label/Light/Tertiary') : C('Label/Light/Primary'),
                  },
                ],
              }}
            />
          )}
        </View>
      </ScrollView>
    </View>
  );
};

export default connect(
  ({ machines: { machinesReferences, machinesReferencesLoaded, machines, machinesLoaded } }) => ({
    machinesReferences,
    machinesReferencesLoaded,
    machines,
    machinesLoaded,
  }),
  {
    getMachinesReferencesBySourcesSystem,
    getMachines,
  }
)(MachinesImnoo);
